import React, { useState, useEffect } from 'react';
import api, { getAccessToken } from '../../services/api';
import { logger } from '../../utils/logger';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  Button,
  Grid,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  WarningAmber as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Upgrade as UpgradeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Types
interface CompanyUsage {
  company_id: string;
  tier: string;
  monthly_pass_limit: number;
  monthly_passes_used: number;
  user_limit: number;
  users_count: number;
  usage_percentage: number;
  approaching_limit: boolean;
  limit_reached: boolean;
}

interface TierUsageProps {
  /**
   * Compact mode reduces size and hides some details
   */
  compact?: boolean;
  /**
   * Show upgrade button for users approaching/at limits
   */
  showUpgradeButton?: boolean;
  /**
   * Callback when refresh is clicked
   */
  onRefresh?: () => void;
  /**
   * Callback when upgrade is clicked
   */
  onUpgrade?: () => void;
  /**
   * Auto-refresh interval in milliseconds (0 = disabled)
   */
  autoRefreshInterval?: number;
}

// Styled components
const StyledCard = styled(Card)<{ status: string }>(({ theme, status }) => {
  let borderColor = theme.palette.success.main;
  let gradientColor = theme.palette.success.main;

  if (status === 'warning') {
    borderColor = theme.palette.warning.main;
    gradientColor = theme.palette.warning.main;
  } else if (status === 'error') {
    borderColor = theme.palette.error.main;
    gradientColor = theme.palette.error.main;
  }

  return {
    position: 'relative',
    overflow: 'visible',
    borderRadius: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'} 100%)`,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.mode === 'dark'
      ? `0 8px 32px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.05)`
      : `0 8px 32px rgba(0,0,0,0.08), inset 0 1px 1px rgba(255,255,255,0.8)`,
    backdropFilter: 'blur(20px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? `0 12px 48px rgba(0,0,0,0.5), 0 0 0 1px ${borderColor}20`
        : `0 12px 48px rgba(0,0,0,0.12), 0 0 0 1px ${borderColor}20`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: `linear-gradient(90deg, ${borderColor}, ${gradientColor}80)`,
      borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
    }
  };
});

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.02)'
    : 'rgba(0,0,0,0.02)',
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledLinearProgress = styled(LinearProgress)<{ status: string }>(({ theme, status }) => {
  let barColor = theme.palette.success.main;

  if (status === 'warning') {
    barColor = theme.palette.warning.main;
  } else if (status === 'error') {
    barColor = theme.palette.error.main;
  }

  return {
    height: 12,
    borderRadius: 8,
    flexGrow: 1,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.08)'
      : 'rgba(0,0,0,0.06)',
    overflow: 'visible',
    '& .MuiLinearProgress-bar': {
      borderRadius: 8,
      background: `linear-gradient(90deg, ${barColor}, ${barColor}CC)`,
      boxShadow: `0 0 12px ${barColor}60`,
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0))',
        borderRadius: 8,
      }
    },
  };
});

const TierChip = styled(Chip)<{ tier: string }>(({ theme, tier }) => {
  const tierColors = {
    free: {
      bg: 'linear-gradient(135deg, #616161 0%, #9E9E9E 100%)',
      color: '#FFFFFF',
      shadow: 'rgba(97, 97, 97, 0.4)'
    },
    basic: {
      bg: 'linear-gradient(135deg, #14264a 0%, #1e3a6f 100%)',
      color: '#FFFFFF',
      shadow: 'rgba(20, 38, 74, 0.4)'
    },
    premium: {
      bg: 'linear-gradient(135deg, #F57C00 0%, #FFB74D 100%)',
      color: '#FFFFFF',
      shadow: 'rgba(245, 124, 0, 0.4)'
    },
    enterprise: {
      bg: 'linear-gradient(135deg, #388E3C 0%, #66BB6A 100%)',
      color: '#FFFFFF',
      shadow: 'rgba(56, 142, 60, 0.4)'
    }
  };

  const colors = tierColors[tier as keyof typeof tierColors] || tierColors.free;

  return {
    background: colors.bg,
    color: colors.color,
    fontWeight: 700,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: theme.spacing(0.5, 2),
    height: 32,
    boxShadow: `0 4px 12px ${colors.shadow}`,
    border: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px) scale(1.02)',
      boxShadow: `0 6px 20px ${colors.shadow}`,
    },
    '& .MuiChip-label': {
      padding: 0,
    }
  };
});

const TierUsageComponent: React.FC<TierUsageProps> = ({
  compact = false,
  showUpgradeButton = true,
  onRefresh,
  onUpgrade,
  autoRefreshInterval = 0
}) => {
  const [usage, setUsage] = useState<CompanyUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Fetch usage data
  const fetchUsage = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAccessToken();
      if (!token) {
        throw new Error('Token za autentifikaciju nije pronađen');
      }

      const response = await api.get('/companies/me/usage');
      const data = response.data;
      setUsage(data);
      setLastUpdated(new Date());
    } catch (err) {
      logger.error('Error fetching usage data:', err);
      setError(err instanceof Error ? err.message : 'Neuspešno učitavanje podataka o korišćenju');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsage();
  }, []);

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefreshInterval > 0) {
      const interval = setInterval(fetchUsage, autoRefreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefreshInterval]);

  // Handle refresh click
  const handleRefresh = () => {
    fetchUsage();
    if (onRefresh) onRefresh();
  };

  // Determine status based on usage
  const getStatus = (usage: CompanyUsage) => {
    if (usage.limit_reached) return 'error';
    if (usage.approaching_limit) return 'warning';
    return 'success';
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <CheckIcon color="success" />;
    }
  };

  // Get status message
  const getStatusMessage = (usage: CompanyUsage) => {
    if (usage.limit_reached) {
      if (usage.tier === 'free') {
        return "Dostigli ste limit za FREE plan (10 passes). Kontaktirajte nas za prelazak na BASIC plan.";
      }
      if (usage.tier === 'basic') {
        return "Približavate se limitu od 500 passes za automatski prelazak na PREMIUM plan (€100/mesec).";
      }
      if (usage.tier === 'premium') {
        return "Približavate se limitu od 5000 passes za automatski prelazak na ENTERPRISE plan (€1000/mesec).";
      }
      return "Dostigli ste mesečni limit za pass-ove.";
    }
    if (usage.approaching_limit) {
      if (usage.tier === 'basic') {
        return "Približavate se limitu. Na 500 passes ćete automatski biti nadograđeni na PREMIUM plan.";
      }
      if (usage.tier === 'premium') {
        return "Približavate se limitu. Na 5000 passes ćete automatski biti nadograđeni na ENTERPRISE plan.";
      }
      return "Približavate se mesečnom limitu za pass-ove.";
    }

    // Show upgrade info for active tiers
    if (usage.tier === 'basic') {
      return `Uključeno 100 passes u €25 planu. Automatski prelazak na PREMIUM na 500 passes.`;
    }
    if (usage.tier === 'premium') {
      return `Uključeno 500 passes u €100 planu. Automatski prelazak na ENTERPRISE na 5000 passes.`;
    }
    if (usage.tier === 'enterprise') {
      return `Uključeno 5000 passes u €1000 planu. Neograničeno nakon toga.`;
    }

    return "Korišćenje je u normalnim okvirima.";
  };

  // Get alert severity based on status
  const getSeverity = (status: string): 'error' | 'warning' | 'success' => {
    if (status === 'error') return 'error';
    if (status === 'warning') return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Učitavanje podataka o korišćenju...</Typography>
            <LinearProgress sx={{ flexGrow: 1 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !usage) {
    return (
      <Card>
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Pokušaj ponovo
              </Button>
            }
          >
            {error || 'Neuspešno učitavanje podataka o korišćenju'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const status = getStatus(usage);
  const passUsageText = `${usage.monthly_passes_used} / ${usage.monthly_pass_limit}`;
  const userUsageText = `${usage.users_count} / ${usage.user_limit}`;

  return (
    <StyledCard status={status}>
      <CardContent sx={{ pb: compact ? 1 : 2 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={compact ? 2 : 3}
          pb={2}
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.03)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {getStatusIcon(status)}
            </Box>
            <Box>
              <Typography
                variant={compact ? "subtitle1" : "h6"}
                component="h2"
                fontWeight={700}
                sx={{ mb: 0.5 }}
              >
                Korišćenje Tier-a
              </Typography>
              <TierChip tier={usage.tier} label={usage.tier.toUpperCase()} size="small" />
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {onRefresh && (
              <Tooltip title="Osveži podatke">
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.03)',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.06)',
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Usage Statistics */}
        <Grid container spacing={compact ? 1 : 2.5}>
          {/* Monthly Passes */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.primary"
              >
                Mesečni Pass-ovi
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #fff 0%, #ccc 100%)'
                    : 'linear-gradient(135deg, #14264a 0%, #1e3a6f 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {passUsageText}
              </Typography>
            </Box>
            <ProgressContainer>
              <StyledLinearProgress
                variant="determinate"
                value={Math.min(usage.usage_percentage, 100)}
                status={status}
              />
            </ProgressContainer>
            {!compact && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {usage.usage_percentage.toFixed(1)}% iskorišćeno
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.04)',
                  }}
                >
                  {usage.monthly_pass_limit - usage.monthly_passes_used} preostalo
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Users */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.primary"
              >
                Aktivni Korisnici
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #fff 0%, #ccc 100%)'
                    : 'linear-gradient(135deg, #388E3C 0%, #66BB6A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {userUsageText}
              </Typography>
            </Box>
            <ProgressContainer>
              <StyledLinearProgress
                variant="determinate"
                value={(usage.users_count / usage.user_limit) * 100}
                status={usage.users_count >= usage.user_limit ? 'error' : 'success'}
              />
            </ProgressContainer>
            {!compact && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {((usage.users_count / usage.user_limit) * 100).toFixed(1)}% popunjeno
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.04)',
                  }}
                >
                  {usage.user_limit - usage.users_count} slobodno
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Status Message and Actions */}
        {!compact && (
          <Box mt={3}>
            <Alert
              severity={getSeverity(status)}
              icon={getStatusIcon(status)}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backdropFilter: 'blur(10px)',
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem'
                }
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {getStatusMessage(usage)}
              </Typography>
            </Alert>

            {showUpgradeButton && (usage.approaching_limit || usage.limit_reached) && (
              <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<UpgradeIcon />}
                  onClick={onUpgrade}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 4px 12px rgba(20, 38, 74, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(20, 38, 74, 0.4)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  Zatraži Unapređenje Plana
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Last Updated */}
        {!compact && lastUpdated && (
          <Box
            mt={2}
            pt={2}
            borderTop="1px solid"
            borderColor="divider"
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
              Poslednje ažuriranje: {lastUpdated.toLocaleTimeString('sr-RS', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default TierUsageComponent;
